import { ChatBubbleOutline, Close, NewspaperOutlined } from '@mui/icons-material';
import { Box, Button, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CompleteIcon from '../../../../assets/baseball-complete.svg';
import IncompleteIcon from '../../../../assets/baseball-incomplete.svg';
import { AnalyticsEvent, useAnalytics } from '../../../../components/Providers/AnalyticsProvider';
import { useFetchData } from '../../../../hooks/useFetchData';
import { selectFolders } from '../../../../redux/reducers/folderReducer';
import { StyledIconButton } from '../../../../theme/CustomComponents';
import { Conversation } from '../../../../Types/conversation';
import { ProjectConfig, ProjectSalesforceInfo } from '../../../../Types/dealGPT';
import { ProjectConfigStatus } from '../../../../Types/enums';
import { projectConfigPathUrl, projectSalesforceInfoPathUrl } from '../../../../util/routesUrls';
import { useDrawer } from '../../../Providers/DrawerProvider';
import ProjectOverviewCards from './ProjectOverviewCards';
import { ProjectOverviewDocxReport } from './ProjectOverviewDocxReport';
import ResearchPrompts from './ResearchPrompts';

export const ProjectConfigStatusColors = {
  [ProjectConfigStatus.COMPLETE]: '#0047FF',
  [ProjectConfigStatus.INCOMPLETE]: '#F52C00',
};

interface ProjectOverviewDrawerProps {
  projectName: string;
}

enum ProjectOverviewTabs {
  OVERVIEW = 0,
  RESEARCH = 1,
}

const ProjectOverviewDrawer: React.FC<ProjectOverviewDrawerProps> = ({ projectName }) => {
  const { currentFolder } = useSelector(selectFolders);
  const { data: salesforce, loading: loadingSalesforce } = useFetchData<ProjectSalesforceInfo>({
    urlPath: projectSalesforceInfoPathUrl(currentFolder?.projectId ?? ''),
  });
  const {
    data: config,
    loading: loadingConfig,
    refetch,
  } = useFetchData<ProjectConfig>({
    urlPath: projectConfigPathUrl(currentFolder?.projectId ?? ''),
  });

  const [currentTab, setCurrentTab] = useState(ProjectOverviewTabs.OVERVIEW);

  const { closeDrawer } = useDrawer();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const onClickViewMore = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_VIEW_MORE,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    navigate(`/folder/${currentFolder?.id}/configure`);
    closeDrawer();
  }, [navigate, closeDrawer, analytics, currentFolder]);

  const generateReport = useCallback(() => {
    if (!config || !salesforce) return;

    const report = new ProjectOverviewDocxReport({
      projectName,
      config,
      salesforce,
    });

    report.generate(`${projectName} Overview.docx`);
  }, [config, salesforce, projectName]);

  const onConversationClick = useCallback(
    (conversation: Conversation) => {
      analytics.event(
        AnalyticsEvent.WM_IA_STARTER_CONVERSATION_CLICK,
        { id: conversation.id },
        {
          folderId: conversation.folderId,
          conversationId: conversation.id,
          projectId: currentFolder?.projectId,
        }
      );

      closeDrawer();
      navigate(`/folder/${conversation.folderId}/${conversation.id}`);
    },
    [analytics, currentFolder, closeDrawer, navigate]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Colored Bar */}
        {!loadingConfig ? (
          <Box
            sx={{
              width: '25px',
              height: '100%',
              backgroundColor:
                ProjectConfigStatusColors[
                  config?.status
                    ? (config.status as ProjectConfigStatus)
                    : ProjectConfigStatus.INCOMPLETE
                ],
            }}
          />
        ) : (
          <Skeleton variant="rectangular" sx={{ width: '25px', height: '100%' }} />
        )}

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            gap: 3,
            maxWidth: 'calc(100% - 24px - 24px - 25px)', // 24px padding on both sides + 25px Colored Bar
            padding: 3,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {!loadingConfig ? (
              <img
                style={{ width: '50px', pointerEvents: 'none' }}
                src={
                  config?.status === ProjectConfigStatus.COMPLETE ? CompleteIcon : IncompleteIcon
                }
              />
            ) : (
              <Skeleton variant="rectangular" sx={{ width: '50px', height: '50px' }} />
            )}
            <Typography variant="h4">{projectName}</Typography>
            <StyledIconButton onClick={closeDrawer}>
              <Close />
            </StyledIconButton>
          </Box>
          <Box>
            <ToggleButtonGroup
              exclusive
              value={currentTab}
              onChange={(_, value) => {
                if (value !== null) {
                  setCurrentTab(value);
                }
              }}
            >
              <ToggleButton value={ProjectOverviewTabs.OVERVIEW} sx={{ padding: 1 }}>
                <NewspaperOutlined htmlColor="#070154" />
              </ToggleButton>
              <ToggleButton value={ProjectOverviewTabs.RESEARCH} sx={{ padding: 1 }}>
                <ChatBubbleOutline htmlColor="#070154" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {currentTab === ProjectOverviewTabs.OVERVIEW && (
            <ProjectOverviewCards
              salesforce={salesforce}
              loadingSalesforce={loadingSalesforce}
              config={config}
              refetch={refetch}
            />
          )}
          {currentTab === ProjectOverviewTabs.RESEARCH && (
            <ResearchPrompts
              folderId={currentFolder?.id}
              projectId={currentFolder?.projectId}
              salesforce={salesforce}
              onConversationClick={onConversationClick}
            />
          )}
        </Box>
      </Box>
      {/* Footer */}
      {currentTab === ProjectOverviewTabs.OVERVIEW && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            width: '100%',
            height: '60px',
            borderTop: '1px solid #ddd',
            gap: 3,
          }}
        >
          <Button variant="outlined" onClick={generateReport}>
            Generate Report
          </Button>
          <Button sx={{ marginRight: 2 }} variant="outlined" onClick={onClickViewMore}>
            View More
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProjectOverviewDrawer;
